import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { JiraConnectorRepo } from '@client/app/feature-modules/shared/jira-connector/services/jira-connector.repo';
import { JiraConnectorSettings } from '@client/app/feature-modules/shared/jira-connector/types/jira-connector.type';
import { stripHtml } from '@client/app/utils/html-strip.util';

@Component({
    selector: 'create-jira-issue-modal',
    standalone: true,
    imports: [CommonModule, FormsModule],
    template: `
        <div class="modal-header">
            <h4 class="modal-title">Create Jira Issue</h4>
            <button type="button" class="btn-close" aria-label="Close" (click)="onDismiss()"></button>
        </div>
        <div class="modal-body">
            <div *ngIf="isLoadingSettings" class="text-center p-4">
                <div class="spinner-border" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>
            <form *ngIf="!isLoadingSettings" #jiraForm="ngForm">
                <div class="mb-3">
                    <label for="jiraProject" class="form-label">Jira Project</label>
                    <input
                        type="text"
                        class="form-control"
                        id="jiraProject"
                        [(ngModel)]="jiraProject"
                        name="jiraProject"
                        readonly
                        disabled
                    />
                </div>
                <div class="mb-3">
                    <label for="title" class="form-label">Title <span class="text-danger">*</span></label>
                    <input
                        type="text"
                        class="form-control"
                        id="title"
                        [(ngModel)]="title"
                        name="title"
                        required
                    />
                </div>
                <div class="mb-3">
                    <label for="description" class="form-label">Description <span class="text-danger">*</span></label>
                    <textarea
                        class="form-control"
                        id="description"
                        rows="8"
                        [(ngModel)]="description"
                        name="description"
                        required
                    ></textarea>
                </div>
            </form>
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="onDismiss()">Cancel</button>
            <button
                type="button"
                class="btn btn-primary"
                (click)="onSubmit()"
                [disabled]="!title || !description || isLoadingSettings"
            >
                Create Issue
            </button>
        </div>
    `,
})
export class CreateJiraIssueModalComponent implements OnInit {
    @Input() resolve!: {
        error: any;
        check: any;
        page: any;
        user: any;
    };
    @Output() close = new EventEmitter<{ title: string; description: string }>();
    @Output() dismiss = new EventEmitter<void>();

    jiraProject = '';
    title = '';
    description = '';
    isLoadingSettings = true;

    constructor (private jiraConnectorRepo: JiraConnectorRepo) {}

    ngOnInit (): void {
        this.loadJiraSettings();
        this.prefillForm();
    }

    loadJiraSettings (): void {
        this.isLoadingSettings = true;
        this.jiraConnectorRepo.getSettings().then(
            (settings: JiraConnectorSettings) => {
                this.jiraProject = settings.project_key || '';
                this.isLoadingSettings = false;
            },
            () => {
                this.isLoadingSettings = false;
            },
        );
    }

    prefillForm (): void {
        const { error, check, page, user } = this.resolve;

        // Prefill title
        const checkName = stripHtml(check.friendly_name || check.name);
        this.title = `Accessibility issue: ${checkName}`;

        // Prefill description
        const pageName = page.title || page.name || 'Unknown Page';
        const codeSnippet = error.source_code || '';
        const userName = user?.getName ? user.getName() : (user?.email || 'Unknown User');

        this.description = `An accessibility violation has been discovered on page ${pageName} for the following code snippet:\n${codeSnippet}

The snippet failed the following Web Governance Accessibility check: ${checkName}. Log into Web Governance for further information and help articles.

Reported by: ${userName}`;
    }

    onSubmit (): void {
        if (this.title && this.description) {
            this.close.emit({
                title: this.title,
                description: this.description,
            });
        }
    }

    onDismiss (): void {
        this.dismiss.emit();
    }
}
